import React, { useContext, createContext, useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import parseISO from 'date-fns/parseISO';
import get from 'lodash/get';
import partition from 'lodash/partition';
import { useQuery } from 'react-query';
import { useGuestAccessHash } from 'utils/hooks/useGuestAccessHash';
import { useParams } from 'react-router-dom';
import { getVirtualSessionsApiClient } from 'lib/SplashApiClient/subclients/VirtualSessionsApiClient';
import { useEditor } from '@splashthat/splash-cms-engine';
import useWaitUntil, { getCurrentDateInISO } from 'utils/hooks/useWaitUntil';
import { mockUpcomingSessions, mockPastSessions, PLACEHOLDER_ACTIVE_SESSION } from '../../mockSessionsData';

const initialAttendeeSessions = {
    sessions: [],
    activeSession: {},
    isLoading: false,
    isError: false,
};

export function byDateAndAlphabetical(a, b) {
    const startTimeDifference = parseISO(a.start) - parseISO(b.start);
    if (startTimeDifference === 0) {
        const endTimeDifference = parseISO(a.end) - parseISO(b.end);
        if (endTimeDifference === 0) {
            return a.name.localeCompare(b.name);
        } else {
            return endTimeDifference;
        }
    } else {
        return startTimeDifference;
    }
}

function sessionEndingInTheFuture(session) {
    const now = getCurrentDateInISO();
    const sessionEndDate = parseISO(session.end);
    return sessionEndDate > now;
}

function classifySessionsByPastAndUpcoming(sessions, shouldUseMockData) {
    let [sessionsInTheFuture, sessionsInThePast] = partition(sessions, sessionEndingInTheFuture);

    if (!sessionsInTheFuture.length && shouldUseMockData) {
        sessionsInTheFuture = mockUpcomingSessions;
    }

    if (!sessionsInThePast.length && shouldUseMockData) {
        sessionsInThePast = mockPastSessions;
    }

    return {
        sessionsInTheFuture,
        sessionsInThePast,
    };
}

const AttendeeSessions = createContext(initialAttendeeSessions);

export const AttendeeSessionProvider = ({ children, eventId }) => {
    const { sessionId } = useParams();
    const virtualSessionsApiClient = useRef(getVirtualSessionsApiClient()).current;
    const editor = useEditor((state) => ({ editing: state.editing }));
    const sortIncomingData = useCallback((response) => response.sort(byDateAndAlphabetical), []);
    const { guestAccessHash } = useGuestAccessHash(`sessionsRegistration/${eventId}/guestAccessHash`);
    const {
        isLoading,
        isError,
        dataUpdatedAt,
        data: persistedSessions = [],
    } = useQuery(
        ['attendeeSessionsData', eventId],
        () => virtualSessionsApiClient.getAttendeeSessions(eventId, guestAccessHash),
        {
            enabled: !!guestAccessHash || editor.editing,
            select: sortIncomingData,
        },
    );
    const [upcomingSessions, setUpcomingSessions] = useState([]);
    const [pastSessions, setPastSessions] = useState([]);

    const filterUpcomingSessions = useCallback(() => {
        const shouldUseFallbackData = !!editor.editing;
        const { sessionsInTheFuture, sessionsInThePast } = classifySessionsByPastAndUpcoming(
            persistedSessions,
            shouldUseFallbackData,
        );

        setUpcomingSessions(sessionsInTheFuture);
        setPastSessions(sessionsInThePast);
    }, [persistedSessions, editor.editing]);

    // Refresh the upcoming sessions if React Query or edition state updates
    useEffect(filterUpcomingSessions, [dataUpdatedAt, editor.editing]);

    // Refresh the upcoming sessions once the first session in the upcoming sessions list completes
    const firstUpcomingSessionEndTime = !editor.editing && get(upcomingSessions, '[0].end');
    useWaitUntil(filterUpcomingSessions, firstUpcomingSessionEndTime);

    // Use persistedSessions so the user can stay in the session after the card is removed from the list
    const session = persistedSessions.find((currentSession) => currentSession.id === Number(sessionId));

    const activeSession = editor.editing ? PLACEHOLDER_ACTIVE_SESSION : session;

    const value = {
        sessions: upcomingSessions,
        persistedSessions,
        pastSessions,
        activeSession,
        isLoading,
        isError,
    };

    return <AttendeeSessions.Provider value={value}>{children}</AttendeeSessions.Provider>;
};

AttendeeSessionProvider.propTypes = {
    children: PropTypes.node,
    eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

AttendeeSessionProvider.defaultProps = {
    children: null,
};

export const useAttendeeSessions = () => useContext(AttendeeSessions);
